<script setup lang="ts">
const error = useError();

interface CustomError {
  url: string;
  statusCode: number;
  statusMessage: string;
  message: string;
  description: string;
  data?: any;
}

const statusCodeError = Number((error.value as CustomError)?.statusCode) || 404;

const authStore = useAuthStore();

onMounted(async () => {
  try {
    await authStore.getUser();
  } catch {
    // Silently handle auth failure on error pages
  }
});

const locale = useNuxtApp().$i18n?.locale?.value || "en";

const errorConfig: Record<number, { icon: string }> = {
  401: { icon: "lock" },
  403: { icon: "lock" },
  404: { icon: "search" },
  500: { icon: "warning" },
  503: { icon: "warning" },
  504: { icon: "clock" },
};

const currentError = computed(() => errorConfig[statusCodeError] || errorConfig[404]);

// The composable exposes `.t`, not `.$t` — and naming this local binding `$t`
// shadowed the template's own global `$t` (every `<script setup>` binding
// takes priority over `globalProperties` of the same name in the compiled
// render), so `{{ $t(...) }}` below resolved to this always-undefined local
// instead of the real translator and threw on every render. Confirmed live:
// this is why the error page itself crashed instead of showing the error,
// leaving a blank screen with no way to see what actually went wrong.
const { t: translate } = useI18n();

function getDescription(code: number): string {
  const desc = translate(`errors.${code}.description`);
  if (typeof desc === 'string') return desc;
  // Fallback for object-based descriptions (text_1 + text_2)
  const t1 = translate(`errors.${code}.description.text_1`);
  const t2 = translate(`errors.${code}.description.text_2`);
  return [t1, t2].filter(t => t && !t.startsWith('errors.')).join(' ');
}

function goHome() {
  clearError({ redirect: `/${locale}` });
}
</script>

<template>
  <NuxtLayout>
    <div class="error-page">
      <div class="error-content">
        <div class="error-icon">
          <MGIcon :icon="currentError.icon" size="3x" />
        </div>
        <h1 class="error-code">{{ statusCodeError }}</h1>
        <h2 class="error-title">{{ $t(`errors.${statusCodeError}.title`) }}</h2>
        <p class="error-description">{{ getDescription(statusCodeError) }}</p>

        <div v-if="statusCodeError === 404" class="error-suggestions">
          <ul>
            <li>{{ $t("errors.404.solutions.a") }}</li>
            <li>{{ $t("errors.404.solutions.b") }}</li>
            <li>{{ $t("errors.404.solutions.c") }}</li>
          </ul>
        </div>

        <button class="btn-home" @click="goHome">
          {{ $t("errors.go_home") || "Go Home" }}
        </button>
      </div>
    </div>
  </NuxtLayout>
</template>

<style lang="scss" scoped>
.error-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 32px;
  width: 100%;
}

.error-content {
  text-align: center;
  max-width: 480px;

  .error-icon {
    color: var(--inactive);
    margin-bottom: 16px;
  }

  .error-code {
    font-size: 72px;
    font-weight: 700;
    color: var(--bt-active);
    line-height: 1;
    margin-bottom: 8px;
  }

  .error-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--active);
    margin-bottom: 12px;
  }

  .error-description {
    font-size: 14px;
    line-height: 22px;
    color: var(--inactive);
    margin-bottom: 24px;
  }

  .error-suggestions {
    text-align: left;
    margin-bottom: 24px;

    ul {
      list-style: disc;
      padding-left: 20px;

      li {
        font-size: 13px;
        line-height: 24px;
        color: var(--inactive);
      }
    }
  }

  .btn-home {
    padding: 10px 32px;
    background: var(--bt-active);
    color: #fff;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
